import { useState } from 'react';
import { useApi } from '../hooks/useApi';
import { DataTable } from '../components/DataTable';
import { Modal } from '../components/Modal';
import { useToast } from '../components/Toast';
import { HelpTip } from '../components/HelpTip';
import { api, extractErrorMessage } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { Button } from '../components/Button';
import { DevolucionesSaldoModal } from '../components/DevolucionesSaldo';
import { montoAPagar } from '../lib/montoAPagar';
import { useHayCambios } from '../hooks/useHayCambios';
import { useCierreConfirmado } from '../hooks/useCierreConfirmado';
import { useCamposObligatorios, faltaSi } from '../hooks/useCamposObligatorios';
import { Obligatorio, LeyendaObligatorio } from '../components/Obligatorio';
import {
  TIPOS_RETENCION_SUFRIDA, ETIQUETAS_TIPO_RETENCION_SUFRIDA, etiquetaTipoRetencionSufrida, requiereJurisdiccion, totalRetenciones,
} from '../lib/retencionSufrida';

interface Cliente { id: number; nombre: string; }
interface Jurisdiccion { id: number; nombre: string; activo: boolean; }
interface RetencionCobro {
  id: number;
  tipo: string;
  jurisdiccionId?: number | null;
  jurisdiccion?: { nombre: string } | null;
  numeroCertificado?: string | null;
  fechaCertificado?: string | null;
  monto: number | string;
}
/** Factura elegida en el cobro (GET /facturas-venta/:id): lo necesario para aplicarle el saldo a favor. */
interface FacturaElegida {
  id: number;
  clienteId: number;
  tipo?: string | null;
  numeroFactura?: string | null;
  estado: string;
  montoTotal: number | string;
  montoCobrado: number | string;
  notasCreditoAplicadas?: number | string | null;
  ordenVenta?: { canal: string } | null;
}
/** Lo que queda por cobrar de una factura: total − notas de crédito aplicadas − cobrado (0 si está cancelada o pagada). */
const pendienteDeCobro = (f: FacturaElegida) => (f.estado === 'cancelada' || f.estado === 'pagada'
  ? 0
  : Math.max(0, Math.round((montoAPagar(f) - Number(f.montoCobrado || 0)) * 100) / 100));
/** Línea del formulario: los campos como texto, como los inputs. */
interface RetencionLinea { tipo: string; jurisdiccionId: string; numeroCertificado: string; fechaCertificado: string; monto: string; }
interface Cobro {
  id: number;
  clienteId: number;
  facturaVentaId?: number;
  fecha: string;
  monto: number;
  medioPago: string;
  referencia?: string;
  retenciones?: RetencionCobro[];
}

const today = () => new Date().toISOString().slice(0, 10);
const emptyForm = () => ({
  clienteId: '', facturaVentaId: '', fecha: today(), monto: 0, medioPago: 'efectivo', referencia: '',
  retenciones: [] as RetencionLinea[],
});
const lineaVacia = (): RetencionLinea => ({ tipo: 'IIBB', jurisdiccionId: '', numeroCertificado: '', fechaCertificado: '', monto: '' });
const pesos = (n: number) => `$ ${n.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const describirRetencion = (r: RetencionCobro) =>
  [etiquetaTipoRetencionSufrida(r.tipo), r.jurisdiccion?.nombre, r.numeroCertificado ? `cert. ${r.numeroCertificado}` : null]
    .filter(Boolean).join(' · ');

export function CobrosPage() {
  const { data, loading, refetch } = useApi<Cobro[]>('/api/v3/cobros');
  const { data: clientes } = useApi<Cliente[]>('/api/v3/clientes');
  const { data: jurisdicciones, error: errorJurisdicciones } = useApi<Jurisdiccion[]>('/api/v3/impositivo/jurisdicciones');
  const jurisdiccionesActivas = (jurisdicciones ?? []).filter(j => j.activo);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const filtered = (data ?? []).filter(row =>
    Object.entries(filters).every(([key, val]) => !val || String((row as any)[key] ?? '').toLowerCase().includes(val.toLowerCase())),
  );
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [submitting, setSubmitting] = useState(false);
  const toast = useToast();
  const { can } = useAuth();
  const [cobroToDelete, setCobroToDelete] = useState<Cobro | null>(null);
  const [deleting, setDeleting] = useState(false);

  const cerrarCobro = () => { setOpen(false); setForm(emptyForm()); };
  const cierreCobro = useCierreConfirmado(useHayCambios(form, open), cerrarCobro);
  // Obligatorios del cobro (backend: CreateCobroDto y validarRetenciones): cliente, fecha y monto (mayor a 0); cada
  // retención, su monto (mayor a 0) y, si es de IIBB, la jurisdicción.
  const obligatorios = useCamposObligatorios(open);
  const totalRetenido = totalRetenciones(form.retenciones);
  const totalAplicado = Math.round((Number(form.monto || 0) + totalRetenido) * 100) / 100;
  const agregarRetencion = () => setForm({ ...form, retenciones: [...form.retenciones, lineaVacia()] });
  const quitarRetencion = (idx: number) => setForm({ ...form, retenciones: form.retenciones.filter((_, i) => i !== idx) });
  const cambiarRetencion = (idx: number, campo: keyof RetencionLinea, valor: string) => setForm({
    ...form,
    retenciones: form.retenciones.map((l, i) => {
      if (i !== idx) return l;
      const nueva = { ...l, [campo]: valor };
      // IVA, Ganancias y SUSS son nacionales: no llevan jurisdicción
      if (campo === 'tipo' && !requiereJurisdiccion(valor)) nueva.jurisdiccionId = '';
      return nueva;
    }),
  });

  const clienteNombre = (id: number) => (clientes ?? []).find(c => c.id === id)?.nombre ?? `#${id}`;
  // Saldo a favor del cliente elegido (notas de crédito que excedieron lo pendiente de su factura): se puede aplicar a
  // la factura elegida antes de registrar el cobro, o devolver en plata (DevolucionesSaldoModal).
  const { data: saldoCliente, refetch: refetchSaldo } = useApi<{ saldoAFavor: number }>(open && form.clienteId ? `/api/v3/clientes/${form.clienteId}/saldo-a-favor` : null);
  const saldoAFavor = Number(saldoCliente?.saldoAFavor ?? 0);
  const { data: facturaLeida, refetch: refetchFactura } = useApi<FacturaElegida>(
    open && form.clienteId && Number(form.facturaVentaId) > 0 ? `/api/v3/facturas-venta/${Number(form.facturaVentaId)}` : null,
  );
  // useApi conserva la respuesta anterior mientras carga otra: solo vale si es la factura escrita
  const facturaElegida = facturaLeida && facturaLeida.id === Number(form.facturaVentaId) ? facturaLeida : null;
  const nombreFactura = facturaElegida ? (facturaElegida.numeroFactura || `#${facturaElegida.id}`) : '';
  const motivoSinAplicar = !facturaElegida ? null
    : facturaElegida.clienteId !== Number(form.clienteId) ? 'La factura no es del cliente elegido'
      : facturaElegida.tipo === 'nota_credito' ? 'Una nota de crédito no recibe saldo a favor'
        : facturaElegida.ordenVenta?.canal === 'mercadolibre' ? 'Las ventas de MercadoLibre las cobra MercadoPago: no reciben saldo a favor'
          : pendienteDeCobro(facturaElegida) <= 0 ? 'La factura no tiene saldo pendiente de cobro'
            : null;
  const [aplicandoSaldo, setAplicandoSaldo] = useState(false);
  const [devolucionesOpen, setDevolucionesOpen] = useState(false);

  // Aplica el saldo a favor a la factura elegida (hasta cubrir lo pendiente) y sugiere cobrar lo que queda
  const aplicarSaldoAFavor = async () => {
    if (!facturaElegida) return;
    setAplicandoSaldo(true);
    try {
      const r = await api.post<{ montoAplicado: number }>(`/api/v3/facturas-venta/${facturaElegida.id}/aplicar-credito`, {});
      toast.addToast(`Saldo a favor aplicado a ${nombreFactura}: ${pesos(Number(r?.montoAplicado ?? 0))}`, 'success');
      const actualizada = await api.get<FacturaElegida>(`/api/v3/facturas-venta/${facturaElegida.id}`);
      setForm(prev => ({ ...prev, monto: pendienteDeCobro(actualizada) }));
      refetchFactura();
      refetchSaldo();
    } catch (e) {
      toast.addToast(extractErrorMessage(e), 'error');
    } finally {
      setAplicandoSaldo(false);
    }
  };

  const handleCreate = async () => {
    if (!obligatorios.validar()) return;
    setSubmitting(true);
    try {
      await api.post('/api/v3/cobros', {
        clienteId: Number(form.clienteId),
        facturaVentaId: form.facturaVentaId ? Number(form.facturaVentaId) : undefined,
        fecha: form.fecha,
        monto: Number(form.monto),
        medioPago: form.medioPago,
        referencia: form.referencia || undefined,
        retenciones: form.retenciones.length > 0
          ? form.retenciones.map(l => ({
            tipo: l.tipo,
            jurisdiccionId: requiereJurisdiccion(l.tipo) && l.jurisdiccionId ? Number(l.jurisdiccionId) : undefined,
            numeroCertificado: l.numeroCertificado.trim() || undefined,
            fechaCertificado: l.fechaCertificado || undefined,
            monto: Number(l.monto),
          }))
          : undefined,
      });
      toast.addToast(
        totalRetenido > 0
          ? `Cobro registrado — ${pesos(Number(form.monto))} a caja y ${pesos(totalAplicado)} aplicados`
          : 'Cobro registrado — ingreso en caja',
        'success',
      );
      cerrarCobro();
      refetch();
    } catch (e) {
      toast.addToast(extractErrorMessage(e), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const tieneRetenciones = (cobroToDelete?.retenciones?.length ?? 0) > 0;

  const handleDelete = async () => {
    if (!cobroToDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/api/v3/cobros/${cobroToDelete.id}`);
      toast.addToast(cobroToDelete.facturaVentaId ? 'Cobro eliminado — saldo devuelto a la factura' : 'Cobro eliminado', 'success');
      setCobroToDelete(null);
      refetch();
    } catch (e) {
      toast.addToast(extractErrorMessage(e), 'error');
    } finally {
      setDeleting(false);
    }
  };

  const columns = [
    { key: 'id', header: '#' },
    { key: 'clienteId', header: 'Cliente', render: (r: Cobro) => clienteNombre(r.clienteId) },
    { key: 'facturaVentaId', header: 'Factura', render: (r: Cobro) => (r.facturaVentaId ? `#${r.facturaVentaId}` : '—') },
    { key: 'fecha', header: 'Fecha', render: (r: Cobro) => String(r.fecha).slice(0, 10) },
    { key: 'medioPago', header: 'Medio' },
    { key: 'monto', header: 'Recibido', render: (r: Cobro) => `$ ${Number(r.monto).toLocaleString('es-AR')}` },
    { key: 'retenciones', header: 'Retenciones', render: (r: Cobro) => {
      const rets = r.retenciones ?? [];
      if (rets.length === 0) return '—';
      return (
        <div className="text-xs space-y-0.5">
          {rets.map(x => (
            <div key={x.id} title={x.fechaCertificado ? `Certificado del ${String(x.fechaCertificado).slice(0, 10)}` : undefined}>
              {pesos(Number(x.monto))} <span className="text-slate-500">{describirRetencion(x)}</span>
            </div>
          ))}
        </div>
      );
    } },
    { key: 'aplicado', header: 'Aplicado', render: (r: Cobro) => pesos(Number(r.monto) + totalRetenciones(r.retenciones)) },
    { key: 'actions', header: '', render: (r: Cobro) => (
      <button
        onClick={() => setCobroToDelete(r)}
        disabled={!can('cobros', 'eliminar')}
        className="text-red-600 hover:text-red-800 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        title={!can('cobros', 'eliminar') ? 'No tenés permiso para realizar esta acción' : 'Eliminar cobro'}
      >
        Eliminar
      </button>
    ) },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 inline">Cobros</h1>
          <HelpTip tips={['Registrá el cobro de un cliente', 'Cada cobro genera un ingreso en la caja (origen: venta)', 'Si se asocia a una factura, actualiza su estado (parcial/pagada)', 'Retenciones que te hicieron (clientes agentes de retención): a la factura se le aplica lo recibido + las retenciones; a la caja entra solo lo recibido', 'Al eliminar un cobro se borra su ingreso en caja y el monto vuelve a quedar pendiente en la factura', 'Si el cliente tiene saldo a favor (notas de crédito), podés aplicarlo a la factura antes de registrar el cobro', 'Devolver saldo a favor registra la devolución en plata al cliente: no modifica facturas ni la caja']} />
          <p className="text-slate-500 text-sm mt-1">Cobranzas — alimentan la caja</p>
        </div>
        <div className="flex gap-2">
        <Button
          variant="secondary"
          onClick={() => setDevolucionesOpen(true)}
          disabled={!can('cobros', 'ver')}
          title={!can('cobros', 'ver') ? 'No tenés permiso para realizar esta acción' : 'Devolver en plata el saldo a favor de un cliente y ver sus devoluciones'}
        >
          Devolver saldo a favor
        </Button>
        <Button
          onClick={() => {
            setForm(emptyForm());
            setOpen(true);
          }}
          disabled={!can('cobros', 'crear')}
          title={!can('cobros', 'crear') ? 'No tenés permiso para realizar esta acción' : undefined}
        >
          + Nuevo cobro
        </Button>
        </div>
      </div>
      <DataTable
        columns={columns}
        data={filtered}
        loading={loading}
        filters={filters}
        onFilterChange={(k, v) => setFilters(f => v ? { ...f, [k]: v } : Object.fromEntries(Object.entries(f).filter(([key]) => key !== k)))}
      />
      <Modal open={open} cierre={cierreCobro} title="Nuevo Cobro" size="lg">
        <div {...obligatorios.contenedor} className="space-y-4">
          <LeyendaObligatorio />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Cliente<Obligatorio /></label>
              <select aria-label="Cliente" required value={form.clienteId} onChange={e => setForm({ ...form, clienteId: e.target.value })} className="w-full px-3 py-2 border rounded-lg">
                <option value="">Elegí un cliente</option>
                {(clientes ?? []).map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
              </select>
              {saldoAFavor > 0 && (
                <p className="text-xs text-emerald-700 mt-1" title="Notas de crédito que excedieron lo pendiente de su factura: se aplica a sus facturas siguientes o se devuelve en plata">
                  {`Saldo a favor del cliente: ${pesos(saldoAFavor)}`}
                  <button
                    type="button"
                    onClick={() => setDevolucionesOpen(true)}
                    disabled={!can('cobros', 'crear')}
                    title={!can('cobros', 'crear') ? 'No tenés permiso para realizar esta acción' : 'Registrar la devolución en plata de este saldo'}
                    className="ml-2 text-blue-600 hover:text-blue-800 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >Devolver saldo a favor</button>
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Factura (opcional)</label>
              <input type="number" placeholder="ID factura" value={form.facturaVentaId} onChange={e => setForm({ ...form, facturaVentaId: e.target.value })} className="w-full px-3 py-2 border rounded-lg" />
              {facturaElegida && (
                <p className="text-xs text-slate-500 mt-1">{`${nombreFactura} · a cobrar ${pesos(pendienteDeCobro(facturaElegida))}`}</p>
              )}
              {facturaElegida && saldoAFavor > 0 && (
                <button
                  type="button"
                  onClick={aplicarSaldoAFavor}
                  disabled={aplicandoSaldo || !can('cobros', 'crear') || !!motivoSinAplicar}
                  title={!can('cobros', 'crear') ? 'No tenés permiso para realizar esta acción'
                    : motivoSinAplicar ?? 'Descuenta el saldo a favor de lo que queda por cobrar de la factura, antes de registrar el cobro'}
                  className="text-xs text-emerald-700 hover:text-emerald-900 font-medium mt-1 disabled:opacity-50 disabled:cursor-not-allowed"
                >{aplicandoSaldo ? 'Aplicando...' : `Aplicar saldo a favor a ${nombreFactura}`}</button>
              )}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Monto<Obligatorio /></label>
              <input type="number" aria-label="Monto" {...faltaSi(!(Number(form.monto) > 0))} value={form.monto} onChange={e => setForm({ ...form, monto: Number(e.target.value) })} className="w-full px-3 py-2 border rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Medio</label>
              <select value={form.medioPago} onChange={e => setForm({ ...form, medioPago: e.target.value })} className="w-full px-3 py-2 border rounded-lg">
                <option value="efectivo">Efectivo</option>
                <option value="transferencia">Transferencia</option>
                <option value="tarjeta">Tarjeta</option>
                <option value="cheque">Cheque</option>
                <option value="mercadopago">MercadoPago</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Fecha<Obligatorio /></label>
              <input type="date" aria-label="Fecha" required value={form.fecha} onChange={e => setForm({ ...form, fecha: e.target.value })} className="w-full px-3 py-2 border rounded-lg" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Referencia</label>
            <input aria-label="Referencia" value={form.referencia} onChange={e => setForm({ ...form, referencia: e.target.value })} className="w-full px-3 py-2 border rounded-lg" />
          </div>
          <div className="border rounded-lg p-3 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <div>
                <p className="text-sm font-medium">Retenciones que te hicieron</p>
                <p className="text-xs text-slate-500">Si el cliente te retuvo impuestos, cargá cada certificado. El monto de arriba es lo que recibiste.</p>
                {errorJurisdicciones && <p className="text-xs text-amber-700 mt-1">No se pudo cargar la lista de jurisdicciones (hace falta el permiso de Impositivo): las retenciones de IIBB no se pueden elegir.</p>}
              </div>
              <button type="button" onClick={agregarRetencion} className="text-sm text-blue-600 hover:text-blue-800 font-medium whitespace-nowrap">+ Agregar retención</button>
            </div>
            {form.retenciones.map((l, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-6 sm:col-span-2">
                  <label className="block text-xs text-slate-600 mb-1">Tipo</label>
                  <select aria-label="Tipo de retención" value={l.tipo} onChange={e => cambiarRetencion(idx, 'tipo', e.target.value)} className="w-full px-2 py-2 border rounded-lg text-sm">
                    {TIPOS_RETENCION_SUFRIDA.map(t => <option key={t} value={t}>{ETIQUETAS_TIPO_RETENCION_SUFRIDA[t]}</option>)}
                  </select>
                </div>
                <div className="col-span-6 sm:col-span-3">
                  <label className="block text-xs text-slate-600 mb-1">Jurisdicción{requiereJurisdiccion(l.tipo) && <Obligatorio />}</label>
                  {requiereJurisdiccion(l.tipo) ? (
                    <select aria-label="Jurisdicción de la retención" required value={l.jurisdiccionId} onChange={e => cambiarRetencion(idx, 'jurisdiccionId', e.target.value)} className="w-full px-2 py-2 border rounded-lg text-sm">
                      <option value="">Elegí</option>
                      {jurisdiccionesActivas.map(j => <option key={j.id} value={j.id}>{j.nombre}</option>)}
                    </select>
                  ) : (
                    <p className="px-2 py-2 text-sm text-slate-400">Nacional</p>
                  )}
                </div>
                <div className="col-span-6 sm:col-span-2">
                  <label className="block text-xs text-slate-600 mb-1">Certificado</label>
                  <input aria-label="Certificado de la retención" value={l.numeroCertificado} onChange={e => cambiarRetencion(idx, 'numeroCertificado', e.target.value)} className="w-full px-2 py-2 border rounded-lg text-sm" />
                </div>
                <div className="col-span-6 sm:col-span-2">
                  <label className="block text-xs text-slate-600 mb-1">Fecha</label>
                  <input type="date" value={l.fechaCertificado} onChange={e => cambiarRetencion(idx, 'fechaCertificado', e.target.value)} className="w-full px-2 py-2 border rounded-lg text-sm" />
                </div>
                <div className="col-span-9 sm:col-span-2">
                  <label className="block text-xs text-slate-600 mb-1">Monto<Obligatorio /></label>
                  <input type="number" min="0" step="0.01" aria-label="Monto de la retención" {...faltaSi(!(Number(l.monto) > 0))} value={l.monto} onChange={e => cambiarRetencion(idx, 'monto', e.target.value)} className="w-full px-2 py-2 border rounded-lg text-sm" />
                </div>
                <div className="col-span-3 sm:col-span-1 text-right">
                  <button type="button" onClick={() => quitarRetencion(idx)} className="text-red-600 hover:text-red-800 text-sm px-2 py-2" title="Quitar retención">Quitar</button>
                </div>
              </div>
            ))}
            {form.retenciones.length > 0 && (
              <p className="text-sm text-slate-700 pt-1">
                Total aplicado a la factura = recibido + retenciones: {pesos(Number(form.monto || 0))} + {pesos(totalRetenido)} = <strong>{pesos(totalAplicado)}</strong>
                <span className="block text-xs text-slate-500">A la caja entra solo lo recibido ({pesos(Number(form.monto || 0))}).</span>
              </p>
            )}
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button onClick={cierreCobro.cerrar} className="px-4 py-2 text-sm">Cancelar</button>
            <Button onClick={handleCreate} loading={submitting}>
              {submitting ? 'Registrando...' : 'Registrar'}
            </Button>
          </div>
        </div>
      </Modal>
      <DevolucionesSaldoModal
        open={devolucionesOpen}
        clienteIdInicial={form.clienteId}
        clientes={clientes ?? []}
        onClose={() => setDevolucionesOpen(false)}
        onCambio={refetchSaldo}
      />
      <Modal open={!!cobroToDelete} onClose={() => setCobroToDelete(null)} title="Eliminar cobro" size="sm">
        <div className="space-y-4">
          <p className="text-slate-700">
            ¿Eliminar este cobro? Se borra su ingreso en caja{tieneRetenciones ? ' y sus retenciones' : ''}{cobroToDelete?.facturaVentaId
              ? ` y ${tieneRetenciones ? 'lo recibido más las retenciones vuelve' : 'el monto vuelve'} a quedar pendiente en la factura`
              : ''}.
          </p>
          {cobroToDelete && (
            <p className="text-sm text-slate-500">
              Cobro #{cobroToDelete.id} — {clienteNombre(cobroToDelete.clienteId)} — $ {Number(cobroToDelete.monto).toLocaleString('es-AR')} del {String(cobroToDelete.fecha).slice(0, 10)}
              {cobroToDelete.facturaVentaId ? ` (factura #${cobroToDelete.facturaVentaId})` : ''}
            </p>
          )}
          <div className="flex justify-end gap-3 pt-2">
            <button onClick={() => setCobroToDelete(null)} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800">Cancelar</button>
            <button
              onClick={handleDelete}
              disabled={deleting || !can('cobros', 'eliminar')}
              title={!can('cobros', 'eliminar') ? 'No tenés permiso para realizar esta acción' : undefined}
              className="bg-red-600 hover:bg-red-500 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >{deleting ? 'Eliminando...' : 'Eliminar'}</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
